import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '../../shared/api/errors';
import { fullTime, pointTime, relativeTime } from '../../shared/lib/format';
import { PageHeader } from '../../shared/ui/layout';
import { Sparkline } from '../../shared/ui/sparkline';
import { StatusBadge } from '../../shared/ui/status-badge';
import { Table, type IColumn } from '../../shared/ui/table';
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NoMatchState,
} from '../../shared/ui/states';
import {
  DEFAULT_ISSUE_FILTERS,
  IssueFilterBar,
  widestIssueWindow,
  type IIssueFilters,
  type TIssueTab,
} from '../../features/issue-filters';
import {
  issueTone,
  listIssues,
  sparkBuckets,
  SPARK_BUCKETS,
  SPARK_WINDOW_MS,
  type TIssue,
} from '../../entities/issue';

const PAGE_SIZE = 100;

const EMPTY: Record<TIssueTab, { title: string; description: string }> = {
  open: {
    title: 'Nothing is failing',
    description:
      'No step and no destination has refused anything. An issue opens by itself the moment one does.',
  },
  acknowledged: {
    title: 'Nothing acknowledged',
    description: 'An issue lands here once somebody acknowledges it: seen, not fixed yet.',
  },
  handled: {
    title: 'Nothing handled yet',
    description: 'An issue lands here once someone marks it handled or ignores it.',
  },
  all: {
    title: 'No issues yet',
    description: 'An issue opens by itself the first time a step fails.',
  },
};

const columns = (now: number, timeZone?: string): IColumn<TIssue>[] => [
  {
    key: 'state',
    header: '',
    width: 'w-col-origin',
    ui: true,
    render: (issue) => <StatusBadge vocabulary="issue" value={issue.state} />,
  },
  {
    key: 'title',
    header: 'Issue',
    width: 'grow',
    ui: true,
    render: (issue) => (
      <span className="truncate text-primary" title={issue.title}>
        {issue.title}
      </span>
    ),
  },
  {
    key: 'flow',
    header: 'Flow',
    width: 'w-col-connection',
    ui: true,
    render: (issue) =>
      issue.flowId ? (
        <span className="truncate" title={issue.flowName ?? undefined}>
          {issue.flowName ?? 'a deleted flow'}
        </span>
      ) : (
        <span className="text-faint">—</span>
      ),
  },
  {
    key: 'step',
    header: 'Step',
    width: 'w-col-destination',
    ui: true,
    render: (issue) => {
      const where = issue.flowId
        ? (issue.node?.name ?? 'a step that is gone')
        : (issue.sourceName ?? '—');
      return (
        <span className="truncate" title={where}>
          {where}
        </span>
      );
    },
  },
  {
    key: 'copies',
    header: 'Copies',
    width: 'w-col-count',
    align: 'right',
    render: (issue) => issue.eventCount.toLocaleString('en-US'),
  },
  {
    key: 'spark',
    header: 'Last 6 hours',
    // `whitespace-nowrap` is load-bearing: without it this header wraps inside a 28px row.
    width: 'w-col-spark whitespace-nowrap',
    render: (issue) => (
      <Sparkline
        values={sparkBuckets(issue.bins, now)}
        tone={issueTone(
          issue.state,
          now - new Date(issue.lastSeen).getTime(),
          SPARK_WINDOW_MS / SPARK_BUCKETS,
        )}
        height="spark"
        label={`${issue.eventCount.toLocaleString('en-US')} failures, last 6 hours`}
      />
    ),
  },
  {
    key: 'since',
    header: 'Since',
    width: 'w-col-when whitespace-nowrap',
    align: 'right',
    render: (issue) => (
      <span title={fullTime(issue.firstSeen, timeZone)}>
        {pointTime(issue.firstSeen, now, timeZone)}
      </span>
    ),
  },
  {
    key: 'lastSeen',
    header: 'Last seen',
    width: 'w-col-when whitespace-nowrap',
    align: 'right',
    ui: true,
    render: (issue) => (
      <span title={fullTime(issue.lastSeen, timeZone)}>
        {relativeTime(issue.lastSeen, now, timeZone)}
      </span>
    ),
  },
];

interface IIssuesPageProps {
  filters: IIssueFilters;
  onFilters: (next: IIssueFilters) => void;
  onOpen: (issue: TIssue) => void;
  timeZone?: string;
}

export const IssuesPage = ({ filters, onFilters, onOpen, timeZone }: IIssuesPageProps) => {
  const now = useMemo(() => Date.now(), [filters]);

  const issues = useQuery({
    queryKey: ['issues', filters.view, filters.flowId ?? null],
    queryFn: () => listIssues({ view: filters.view, flowId: filters.flowId, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  // Narrowed here because `GET /v1/issues` does not take a window: by when it was last seen.
  const all = issues.data?.data;
  const rows = useMemo(() => {
    const since = now - filters.minutes * 60_000;
    return (all ?? []).filter((issue) => new Date(issue.lastSeen).getTime() >= since);
  }, [all, filters.minutes, now]);
  const narrowed = (all?.length ?? 0) > rows.length;
  const forbidden = issues.error instanceof ApiError && issues.error.status === 403;

  const body = () => {
    if (forbidden) return <ForbiddenState needs="events:read" />;
    const failed = issues.error ?? (issues.isPending ? issues.failureReason : null);
    if (failed) return <ErrorState error={failed} onRetry={() => void issues.refetch()} />;
    if (issues.isPending) return <LoadingState rows={8} />;
    if (rows.length === 0) {
      if (narrowed || filters.flowId) {
        return (
          <NoMatchState
            onReset={() => onFilters({ ...filters, flowId: undefined, minutes: widestIssueWindow })}
          />
        );
      }
      return <EmptyState {...EMPTY[filters.view]} />;
    }
    return (
      <Table
        layoutId="issues-by-step"
        columns={columns(now, timeZone)}
        rows={rows}
        rowKey={(issue) => issue.id}
        muted={(issue) => issue.state === 'resolved' || issue.state === 'ignored'}
        onRowClick={onOpen}
      />
    );
  };

  return (
    <>
      <PageHeader
        width="full"
        title="Issues"
        description="Grouped problems, each tied to the step where it happens."
      />

      <IssueFilterBar
        filters={filters}
        onChange={(next) => onFilters({ ...DEFAULT_ISSUE_FILTERS, ...next })}
        counts={issues.data?.counts}
        note={issues.data?.note}
      />

      <div className="px-page">
        <div className={rows.length === 0 ? 'py-6' : undefined}>{body()}</div>
      </div>
    </>
  );
};
